import { colors, images, intl, ui, UUID } from '@beelday/common'
import styled from '@emotion/styled'
import { ChangeEvent, FC, KeyboardEvent, useCallback, useState } from 'react'

const InputContainer = styled.div`
	position: relative;
	width: 100%;
	display: flex;
	margin-top: auto;
	padding-top: 10px;
`

const Input = styled.input`
	padding: 11px 15px 11px 15px;
	border-radius: 0 0 0 7px;
	border: 1px solid ${colors.heather30};
	width: 100%;
	height: 100%;
	font-size: 14px;
	line-height: 1;

	${ui.responsive.desktop1440} {
		padding: 20px 15px 20px 15px;
	}

	&:disabled {
		background-color: ${ui.colors.white};
	}

	&:focus {
		outline: none;
	}
`

const Button = styled.button`
	align-items: center;
	background-color: ${ui.colors.greenishCyan};
	border: none;
	border-radius: 0 0 7px 0;
	cursor: pointer;
	display: flex;
	justify-content: center;
	min-width: 50px;
	padding: 15px 0;
	transition: opacity 0.25s;

	${ui.responsive.desktop1440} {
		min-width: 68px;
		padding: 18px 0;
	}

	img {
		width: 20px;
		height: 20px;
	}

	&:hover {
		opacity: 0.7;
	}

	&:disabled {
		pointer-events: none;
		opacity: 0.5;
	}
`

type Props = {
	questionId: UUID
	/** A one-answer poll I have answered: the field has nothing more to take. */
	answerGiven: boolean
	onSubmit: (questionId: UUID, text: string) => unknown
}

/** Where one answers a question of a list poll: Enter or the button sends it. */
export const ListAnswerField: FC<Props> = ({
	questionId,
	answerGiven,
	onSubmit,
}) => {
	const [word, setWord] = useState('')
	const translate = intl.useTranslate()

	const send = useCallback(() => {
		onSubmit(questionId, word)
		setWord('')
	}, [onSubmit, questionId, word])

	const keyDownListener = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter' && word) {
				send()
				e.preventDefault()
			}
		},
		[send, word]
	)

	return (
		<InputContainer>
			<Input
				type="text"
				onKeyDown={keyDownListener}
				value={word}
				onChange={(event: ChangeEvent<HTMLInputElement>) =>
					setWord(event.target.value)
				}
				placeholder={translate(
					answerGiven
						? 'polling.open_ended.standard_list.answer.given'
						: 'polling.open_ended.standard_list.answer.placeholder'
				)}
				disabled={answerGiven}
			/>
			<Button
				type="button"
				aria-label={translate('polling.open_ended.standard_list.answer.send')}
				disabled={answerGiven || !word}
				onClick={send}
			>
				<img src={images.send} alt="" />
			</Button>
		</InputContainer>
	)
}
